import React, { useState } from 'react';
import {
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, TextField, Stack, Alert, CircularProgress,
} from '@mui/material';
import { StopCircleOutlined } from '@mui/icons-material';
import axios from 'axios';
import { logAudit } from '../utils/audit';

interface CancelPipelineButtonProps {
  /**
   * The FS App Manager build id. The worker resolves this to the GitLab
   * pipeline id server-side (mirrors the retry endpoint). The activity-feed
   * payload doesn't always carry the GitLab pipeline id, but the FS build id
   * is always available, so we standardise on it here.
   */
  buildId: string | number | undefined;
  /** What to show next to "Cancel pipeline" — usually the composer host. */
  label?: string;
  /** Optional version / platform shown in the confirm dialog for clarity. */
  version?: string;
  platform?: string;
  /** Called after the cancel call succeeds, so the parent can refresh its feed. */
  onCancelled?: () => void;
  /** Compact mode shrinks the button — used inside dense DataGrid rows. */
  size?: 'small' | 'medium';
}

/**
 * Drop-in icon button that cancels a running GitLab pipeline. Opens a confirm
 * dialog with an optional reason field (logged in the audit metadata), then
 * hits the backend proxy `/api/builds/gitlab/pipelines/:id/cancel`.
 *
 * Render only on rows whose status is queued / running / pending — the parent
 * is responsible for the visibility gate.
 */
export const CancelPipelineButton: React.FC<CancelPipelineButtonProps> = ({
  buildId, label, version, platform, onCancelled, size = 'small',
}) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Numeric, positive FS build ids only. "pending...", undefined, or non-
  // numeric values mean the worker hasn't acknowledged the trigger yet —
  // there's nothing to cancel. The worker validates the same shape, but
  // gating client-side keeps the button disabled instead of letting users
  // click into a guaranteed 400.
  const numericBuildId = Number(buildId);
  const disabled = !buildId || !Number.isFinite(numericBuildId) || numericBuildId <= 0;

  const handleConfirm = async () => {
    if (disabled) return;
    setSubmitting(true);
    setError(null);
    try {
      const tok = localStorage.getItem('token');
      const resp = await axios.post(
        `/api/builds/gitlab/pipelines/${encodeURIComponent(String(numericBuildId))}/cancel`,
        {},
        { headers: tok ? { Authorization: `Bearer ${tok}` } : {} },
      );
      logAudit({
        action: 'cancel',
        targetType: 'pipeline',
        // Resolved GitLab pipeline id, if the worker sent one back; falls
        // back to the build id so the audit row is never empty.
        targetId: String(resp.data?.id ?? numericBuildId),
        metadata: {
          buildId: numericBuildId,
          gitlabPipelineId: resp.data?.id,
          composerHost: label,
          version,
          platform,
          reason: reason.trim() || undefined,
        },
      });
      setOpen(false);
      setReason('');
      onCancelled?.();
    } catch (err: any) {
      // Surface what GitLab / the worker actually said — generic "Failed to
      // cancel pipeline" leaves the operator with nothing to act on.
      const data = err?.response?.data || {};
      const status = err?.response?.status;
      const parts: string[] = [];
      if (status) parts.push(`HTTP ${status}`);
      if (data.error) parts.push(data.error);
      if (data.details?.error) parts.push(String(data.details.error));
      else if (typeof data.details === 'string') parts.push(data.details.slice(0, 200));
      setError(parts.length > 0 ? parts.join(' — ') : (err?.message || 'Failed to cancel pipeline.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip title={disabled ? 'Build id not available yet' : 'Cancel this running pipeline'} arrow>
        <span>
          <IconButton
            size={size}
            disabled={disabled}
            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
            sx={{
              color: '#b91c1c',
              bgcolor: 'rgba(220, 38, 38, 0.08)',
              '&:hover': { bgcolor: 'rgba(220, 38, 38, 0.16)' },
              '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.26)', bgcolor: 'transparent' },
            }}
            aria-label="Cancel pipeline"
          >
            <StopCircleOutlined fontSize={size === 'small' ? 'small' : 'medium'} />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => !submitting && setOpen(false)}
        maxWidth="xs"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Cancel this pipeline?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              GitLab will be asked to stop the pipeline for build <strong>#{numericBuildId}</strong>
              {label ? <> · <strong>{label}</strong></> : null}
              {version ? <> · v{version}</> : null}
              {platform ? <> · {platform}</> : null}.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Cancelling is final. The build will be marked Cancelled in the activity feed once GitLab confirms.
            </Typography>
            <TextField
              label="Reason (optional)"
              placeholder="e.g. wrong version, duplicate trigger, infra blip…"
              fullWidth
              size="small"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
              multiline
              minRows={2}
              inputProps={{ maxLength: 280 }}
              helperText="Stored in the audit log so future operators have context."
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={submitting}>
            Keep running
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting || disabled}
            variant="contained"
            color="error"
            startIcon={submitting ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <StopCircleOutlined />}
          >
            {submitting ? 'Cancelling…' : 'Cancel pipeline'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CancelPipelineButton;
